import {
  DataTableColumn,
  FormDialog as CustomFormDialog,
  FormDialogMode,
  formDialogProps,
  InputStyle,
  Section
} from '@globaleris/ui.library'
import React from 'react'
import { useReferredState } from './useReferredState'

export interface FormDialogDefinitionProps {
  id: string
  inputs?: DataTableColumn[]
  sections?: Section[]
}

export interface FormDialogHookProps {
  definition: FormDialogDefinitionProps
  inputs?: DataTableColumn[]
  sections?: Section[]
  inputData?: any
  open?: boolean
  isWaiting?: boolean
  onClose: (formData: any) => void
  title?: string
  subtitle?: string | React.ReactNode
  mode?: FormDialogMode
  okBtnLabel?: string
  cancelBtnLabel?: string
  closeBtnLabel?: string
  inputType?: InputStyle
}

export interface FormDialogHookResult {
  openForm: (props: FormDialogHookProps) => void
  getFormData: (id: any) => any
  updateFormDefinition: (definition: any, data?: any) => void
  updateFormData: (data: any) => void
  closeForm: () => void
  setFormWaiting: (isWaiting: boolean) => void
  formProps: formDialogProps
  formHookProps: FormDialogHookProps
  formHookPropsRef: FormDialogHookProps
  CustomFormDialog?: any
  setFormSubtitle: (subtitle: string | React.ReactNode) => void
  setFormMode: (mode: FormDialogMode) => void
}

const initialFormDialogProps = {
  definition: { id: '', inputs: [], sections: [] },
  inputs: [],
  inputData: null,
  mode: FormDialogMode.ADD,
  open: false,
  onClose: () => {}
}

export const useFormDialog = () => {
  let [formProps, formPropsRef, setFormProps] =
    useReferredState<formDialogProps>(initialFormDialogProps)

  let [formHookProps, formHookPropsRef, setFormHookProps] =
    useReferredState<FormDialogHookProps>(initialFormDialogProps)

  React.useEffect(() => {
    setFormProps({
      inputs: formHookPropsRef.current.definition.inputs,
      sections: formHookPropsRef.current.definition.sections,
      inputData: formHookPropsRef.current.inputData,
      mode: formHookPropsRef.current.mode,
      open: formHookPropsRef.current.open!,
      title: formHookPropsRef.current.title,
      subtitle: formHookPropsRef.current.subtitle,
      isWaiting: formHookPropsRef.current.isWaiting,
      okBtnLabel: formHookPropsRef.current.okBtnLabel,
      cancelBtnLabel: formHookPropsRef.current.cancelBtnLabel,
      closeBtnLabel: formHookPropsRef.current.closeBtnLabel,
      inputType: formHookPropsRef.current.inputType,
      onClose: formHookPropsRef.current.onClose
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formHookProps])

  const updateFormDefinition = React.useCallback(
    (definition: any, data?: any) => {
      definition.id === formHookPropsRef.current.definition.id &&
        setFormHookProps({
          ...formPropsRef.current,
          definition: definition,
          inputs: definition.inputs,
          sections: definition.sections,
          inputData: data !== undefined ? data : formPropsRef.current.inputData,
          isWaiting: false
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  const updateFormData = React.useCallback(
    (data: any) => {
      setFormHookProps({
        ...formPropsRef.current,
        definition: formHookPropsRef.current.definition,
        inputData: data !== undefined ? data : formPropsRef.current.inputData
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  const getFormData = (id: any) => {
    return id === formHookPropsRef.current.definition.id ? formHookPropsRef.current.inputData : null
  }

  const openForm = React.useCallback(
    (props: FormDialogHookProps) => {
      setFormHookProps({ ...props, open: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  const closeForm = React.useCallback(() => {
    setFormHookProps(initialFormDialogProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFormProps])

  const setFormWaiting = React.useCallback(
    isWaiting => {
      setFormHookProps({
        ...formHookPropsRef.current,
        isWaiting: isWaiting
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  const setFormSubtitle = React.useCallback(
    subtitle => {
      setFormHookProps({
        ...formHookPropsRef.current,
        subtitle: subtitle
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  const setFormMode = React.useCallback(
    mode => {
      setFormHookProps({
        ...formHookPropsRef.current,
        mode: mode
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFormProps]
  )

  return {
    openForm,
    updateFormDefinition,
    updateFormData,
    getFormData,
    closeForm,
    setFormWaiting,
    formProps,
    formHookProps,
    formHookPropsRef: formHookPropsRef?.current,
    CustomFormDialog,
    setFormSubtitle,
    setFormMode
  } as FormDialogHookResult
}
